/**
 * PortalTheme — bridges theme styling across portal boundaries.
 *
 * Lattice's portalled primitives (Popover, Menu, Tooltip, Modal, Sheet, Drawer,
 * Combobox) mount their content to `document.body` via a Portal. Any theme set
 * by setting CSS custom properties on an ancestor element — as the agent-ui does
 * on its shell `<div>` — does not reach that content, because the portal escapes
 * the subtree the variables were scoped to.
 *
 * A surface that scopes its theme to an element (rather than to `:root`, where
 * inheritance would cover portals for free) wraps its tree in
 * `PortalThemeProvider`, publishing the same CSS variables and identifying
 * attributes into context. Each portal primitive renders a `PortalThemeScope` as
 * the first child inside its Portal, re-applying them so portalled content
 * inherits the surface's theme. React context crosses portals, so the values
 * reach `document.body` even though the DOM ancestor does not.
 *
 * Surfaces that theme via `:root` (mini-apps in their own iframe, Storybook in
 * its preview frame) need not use this — their portals already inherit. There,
 * the context is empty and `PortalThemeScope` renders a transparent passthrough.
 */
import * as React from "react";

import { createContext } from "replo-utils/react/context";

interface PortalThemeValue {
  /** CSS custom properties to re-apply inside the portal (e.g. `--color-*`). */
  style: React.CSSProperties;
  /** Identifying attributes to mirror onto portalled content (e.g. surface markers). */
  attributes: Record<string, string | undefined>;
}

const emptyValue: PortalThemeValue = { style: {}, attributes: {} };

const PortalThemeContext = createContext<PortalThemeValue>(
  "PortalThemeContext",
  emptyValue,
);

export function PortalThemeProvider({
  style,
  attributes,
  children,
}: React.PropsWithChildren<Partial<PortalThemeValue>>) {
  const parentValue = React.useContext(PortalThemeContext);
  const value = React.useMemo<PortalThemeValue>(
    () => ({
      style: style ? { ...parentValue.style, ...style } : parentValue.style,
      attributes: attributes
        ? { ...parentValue.attributes, ...attributes }
        : parentValue.attributes,
    }),
    [attributes, parentValue, style],
  );
  return (
    <PortalThemeContext.Provider value={value}>
      {children}
    </PortalThemeContext.Provider>
  );
}

/**
 * Re-applies the in-scope portal theme to its children. Renders a
 * `display: contents` wrapper so it carries the CSS variables and attributes
 * without introducing a layout box — the child positioner/backdrop stays a
 * direct-enough descendant of the portal for positioning to behave.
 */
export function PortalThemeScope({ children }: React.PropsWithChildren) {
  const { style, attributes } = React.useContext(PortalThemeContext);
  return (
    <div style={{ display: "contents", ...style }} {...attributes}>
      {children}
    </div>
  );
}

/**
 * The DOM node lattice's portalled primitives mount into. `null` means the
 * default (`document.body`). A surface publishes its own node so the overlay
 * inherits the surface's visibility (opacity/inert) and can't paint over a
 * sibling surface — see the agent browser's per-tab layer.
 */
const PortalContainerContext = createContext<HTMLElement | null>(
  "PortalContainerContext",
  null,
);

export function PortalContainerProvider({
  container,
  children,
}: React.PropsWithChildren<{ container: HTMLElement | null }>) {
  return (
    <PortalContainerContext.Provider value={container}>
      {children}
    </PortalContainerContext.Provider>
  );
}

/** Container for lattice portals; `undefined` (the Base UI default) means `document.body`. */
export function usePortalContainer(): HTMLElement | undefined {
  return React.useContext(PortalContainerContext) ?? undefined;
}
